import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import useSwap from './useSwap';
import { TOKEN_LIST } from './constants';
import './SwapComponent.css';

// Helper function to format balances with 2 decimal places
const formatBalance = (balance) => {
  if (!balance || balance === '') return '0';
  
  // Convert to number and format with 2 decimal places
  const numBalance = parseFloat(balance);
  if (isNaN(numBalance)) return '0';
  
  // Return with 2 decimal places
  return numBalance.toFixed(2);
};

const SwapComponent = ({ account: parentAccount, connectWallet: parentConnectWallet }) => {
  const { t } = useTranslation(['common', 'swap']);
  
  // Initialize with different tokens
  const [fromToken, setFromToken] = useState(TOKEN_LIST[0]);
  const [toToken, setToToken] = useState(TOKEN_LIST[1]);
  const [inputAmount, setInputAmount] = useState('');
  const [outputAmount, setOutputAmount] = useState('');
  const [swapRoute, setSwapRoute] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [fromTokenBalance, setFromTokenBalance] = useState('0');
  const [toTokenBalance, setToTokenBalance] = useState('0');
  const [isQuoteFetching, setIsQuoteFetching] = useState(false);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  
  // Define token select button style (matching the Liquidity component style)
  const tokenSelectStyle = {
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(to right, #6472F3, #4B5EF7)',
    border: 'none',
    borderRadius: '12px',
    padding: '8px 12px',
    color: 'white',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(76, 110, 245, 0.2)'
  };
  
  // Use the useSwap hook to get all functions
  const {
    account: hookAccount,
    loading,
    error,
    connectWallet: hookConnectWallet,
    getQuote,
    executeSwap,
    getTokenBalance,
  } = useSwap();

  // Use account from hook instead of parent if available
  const account = hookAccount || parentAccount;
  const connectWallet = hookAccount ? hookConnectWallet : parentConnectWallet;

  // Function to swap between fromToken and toToken
  const switchTokens = () => {
    // Swap the tokens
    const tempToken = fromToken;
    setFromToken(toToken);
    setToToken(tempToken);
    
    // Also swap the input and output amounts if they exist
    if (inputAmount && outputAmount) {
      setInputAmount(outputAmount);
      // Note: outputAmount will be recalculated through the quote effect
    }
  };

  // Ensure tokens are different when component initializes
  useEffect(() => {
    // Make sure fromToken and toToken are always different
    if (fromToken.address === toToken.address) {
      // If only 2 tokens available, use them
      if (TOKEN_LIST.length >= 2) {
        if (fromToken.address === TOKEN_LIST[0].address) {
          setToToken(TOKEN_LIST[1]);
        } else {
          setToToken(TOKEN_LIST[0]);
        }
      }
    }
  }, []);
  

  // Listen for account changes
  useEffect(() => {
    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0 || accounts[0] !== account) {
        window.location.reload();
      }
    };

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, [account]);
  
  // Fetch token balances
  useEffect(() => {
    const fetchBalances = async () => {
      if (account && fromToken && toToken && getTokenBalance) {
        try {
          setIsBalanceLoading(true);
          const fromBalance = await getTokenBalance(fromToken.address, account);
          const toBalance = await getTokenBalance(toToken.address, account);
          
          // Format balances with 2 decimal places
          setFromTokenBalance(formatBalance(fromBalance));
          setToTokenBalance(formatBalance(toBalance));
        } catch (error) {
          console.error('Error fetching balances:', error);
        } finally {
          setIsBalanceLoading(false);
        }
      }
    };
    fetchBalances();
  }, [account, fromToken, toToken, getTokenBalance]);

  // Ensure tokens are always different any time they change
  useEffect(() => {
    if (fromToken && toToken && fromToken.address === toToken.address) {
      // Find a different token
      const differentToken = TOKEN_LIST.find(token => token.address !== fromToken.address);
      
      if (differentToken) {
        setToToken(differentToken);
      } else if (TOKEN_LIST.length >= 2) {
        // If no different token found (shouldn't happen with 2+ tokens)
        // But just in case, use the first two tokens
        setFromToken(TOKEN_LIST[0]);
        setToToken(TOKEN_LIST[1]);
      }
    }
  }, [fromToken, toToken]);

  // Fetch quote for swap with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const fetchQuote = async () => {
        if (inputAmount && parseFloat(inputAmount) > 0) {
          setIsQuoteFetching(true);
          try {
            const quoteResult = await getQuote(fromToken, toToken, inputAmount);
            if (quoteResult && quoteResult.quote) {
              // Parse the quote but don't force decimal format
              const quoteValue = parseFloat(quoteResult.quote);
              
              // If it's a whole number, display without decimal
              if (quoteValue % 1 === 0) {
                setOutputAmount(quoteValue.toString());
              } else {
                // Otherwise use the quote directly
                setOutputAmount(quoteResult.quote);
              }
              
              setSwapRoute(quoteResult.route);
            } else {
              setOutputAmount('');
              setSwapRoute(null);
            }
          } catch (error) {
            console.error('Error fetching quote:', error);
            setOutputAmount('');
            setSwapRoute(null);
          } finally {
            setIsQuoteFetching(false);
          }
        } else {
          setOutputAmount('');
          setSwapRoute(null);
        }
      };

      fetchQuote();
    }, 500); // Wait 500ms after typing stops before fetching quote

    return () => clearTimeout(debounceTimer);
  }, [inputAmount, fromToken, toToken, getQuote]);

  // Handle swap execution
  const handleSwap = async () => {
    if (!swapRoute) return;
    
    try {
      const result = await executeSwap(swapRoute, fromToken);
      if (result.success) {
        setTxHash(result.hash);
        
        // Add transaction to history
        if (window.addTransaction) {
          window.addTransaction({
            type: 'SWAP',
            fromToken: fromToken.symbol,
            toToken: toToken.symbol,
            fromAmount: inputAmount,
            toAmount: outputAmount,
            hash: result.hash,
            timestamp: Date.now()
          });
        }
      
        // Reset input after successful swap
        setInputAmount('');
        setOutputAmount('');
        
        // Refresh token balances
        if (account) {
          setIsBalanceLoading(true);
          try {
            const fromBalance = await getTokenBalance(fromToken.address, account);
            const toBalance = await getTokenBalance(toToken.address, account);
            
            setFromTokenBalance(formatBalance(fromBalance));
            setToTokenBalance(formatBalance(toBalance));
          } catch (error) {
            console.error('Error refreshing balances:', error);
          } finally {
            setIsBalanceLoading(false);
          }
        }
      }
    } catch (error) {
      console.error('Swap execution error:', error);
    }
  };

  // Simple input handler - no complex logic
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow empty string or valid numbers
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setInputAmount(value);
    }
  };

  // Generate wallet info text with proper address truncation
  const walletInfoText = account ? 
    t('swap:walletInfo', { 
      address: `${account.substring(0, 6)}...${account.substring(account.length - 4)}`
    }) : '';

  return (
    <div className="dex-container">
      <h1>{t('swap:title')}</h1>
      
      {error && <div className="error-message">{error}</div>}
      {txHash && (
        <div className="success-message">
          {t('common:success.txSuccess')}
          <a 
            href={`https://sepolia.etherscan.io/tx/${txHash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('common:success.viewEtherscan')}
          </a>
        </div>
      )}
      
      {!account && (
        <div className="warning-message">
          {t('common:warnings.swapConnect')}
        </div>
      )}
      
      <div className="swap-card">
        {/* From Token Section */}
        <div className="token-input-container">
          <div className="token-label">{t('swap:labels.from')}</div>
          <div className="token-input-row">
            <input
              type="text"
              placeholder="0"
              value={inputAmount}
              onChange={handleInputChange}
              disabled={loading || !account}
            />
            <button 
              style={tokenSelectStyle}
              onClick={switchTokens}
              disabled={loading || !account}
              title={t('swap:buttons.switchTokens')}
            >
              {fromToken.symbol}
              <span style={{ marginLeft: '6px', fontSize: '12px' }}>▼</span>
            </button>
          </div>
          {account && (
            <div className="balance-display">
              {t('swap:labels.balance')}: {isBalanceLoading ? 
                t('common:loading.updating') : 
                fromTokenBalance} 
              {fromToken.symbol}
            </div>
          )}
        </div>
        
        {/* Switch Button */}
        <button 
          className="switch-button" 
          onClick={switchTokens}
          disabled={loading || !account}
          title={t('swap:buttons.switchTokens')}
        >
          ↑↓
        </button>
        
        {/* To Token Section */}
        <div className="token-input-container">
          <div className="token-label">{t('swap:labels.to')}</div>
          <div className="token-input-row">
            <input
              type="text"
              placeholder={isQuoteFetching ? t('common:loading.fetching') : "0"}
              value={isQuoteFetching ? "" : outputAmount}
              readOnly
              disabled={true}
              className={isQuoteFetching ? "fetching" : ""}
            />
            <button 
              style={tokenSelectStyle}
              onClick={switchTokens}
              disabled={loading || !account}
              title={t('swap:buttons.switchTokens')}
            >
              {toToken.symbol}
              <span style={{ marginLeft: '6px', fontSize: '12px' }}>▼</span>
            </button>
          </div>
          {account && (
            <div className="balance-display">
              {t('swap:labels.balance')}: {isBalanceLoading ? 
                t('common:loading.updating') : 
                toTokenBalance} 
              {toToken.symbol}
            </div>
          )}
        </div>
        
        {/* Action Button (Swap or Connect) */}
        {account ? (
          <button 
            className="swap-button"
            onClick={handleSwap}
            disabled={loading || !swapRoute || !inputAmount || fromToken.address === toToken.address}
          >
            {loading ? t('common:loading.processing') : 
              (fromToken.address === toToken.address ? 
                t('swap:buttons.invalidPair') : 
                t('swap:buttons.swap'))}
          </button>
        ) : (
          <button 
            className="connect-button"
            onClick={connectWallet}
            disabled={loading}
          >
            {t('common:buttons.connect')}
          </button>
        )}
        
        {/* Connected Wallet Info */}
        {account && (
          <div className="wallet-info">
            {walletInfoText}
          </div>
        )}
      </div>
    </div>
  );
};

export default SwapComponent;